/**
 * Перенесення в харчування: діловод обирає, які позначки з таблиць нарядів і
 * статусу о/с лягають у харчування за показані дні, і чи ставити компенсацію
 * за ранок після добового. Біля кожного пункту — скільки клітинок він змінить,
 * щоб до натискання було видно, що станеться.
 *
 * На виду лише те, що цього разу щось змінить. Пункти без змін згорнуті в
 * один рядок літер, а прийоми їжі позначки ховаються за коротким описом —
 * їх міняють рідко, і ряд кнопок під кожною позначкою топив сам вибір.
 */

import {
  addDays,
  awayKindsOf,
  paintSpreadOf,
  startMealOf,
  transferableKindsOf,
  transferIntoMealsOps,
  type IsoDate,
  type MarkTypeDef,
  type Person,
} from '@kancelaria/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorText } from '../../api/client.js';
import { useData } from '../../store/data.js';
import { useLockCheck } from '../../store/lock.js';
import { useMarks } from '../../store/marks.js';
import { toast } from '../../store/ui.js';
import { DateInput } from '../DatePicker.js';
import { Field, Modal } from '../ui.js';

/** Вибір пам'ятаємо: щомісяця переносять те саме. */
const PICK_KEY = 'kanc.transferKinds';

/** Пункт «компенсація після добового» живе у виборі поруч із кодами позначок. */
const AFTER_DUTY = '@afterDuty';

/** «1 клітинка», «54 клітинки», «15 клітинок». */
function cellsText(n: number): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? 'клітинка' : d >= 2 && d <= 4 && (h < 12 || h > 14) ? 'клітинки' : 'клітинок';
  return `${n} ${word}`;
}

/** Без збереженого вибору галочки зняті: переносять лише те, що відмітили. */
function readPick(all: string[]): Set<string> {
  try {
    const saved = JSON.parse(localStorage.getItem(PICK_KEY) ?? 'null') as unknown;
    if (Array.isArray(saved)) return new Set(saved.filter((k): k is string => all.includes(String(k))));
  } catch {
    /* немає сховища — нічого не вибрано */
  }
  return new Set();
}

function savePick(pick: Set<string>): void {
  try {
    localStorage.setItem(PICK_KEY, JSON.stringify([...pick]));
  } catch {
    /* не запам'ятали — наступного разу знову порожньо */
  }
}

/**
 * Прийоми їжі, які переносить позначка: «код → номери прийомів». Немає
 * запису — усі. Пам'ятаємо так само, як і вибір позначок.
 */
const MEALS_KEY = 'kanc.transferMeals';
type MealPick = Record<string, number[]>;

function readMeals(): MealPick {
  try {
    const saved = JSON.parse(localStorage.getItem(MEALS_KEY) ?? 'null') as unknown;
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      return Object.fromEntries(
        Object.entries(saved as Record<string, unknown>)
          .filter(([, v]) => Array.isArray(v))
          .map(([k, v]) => [k, (v as unknown[]).map(Number).filter(Number.isInteger)]),
      );
    }
  } catch {
    /* немає сховища — усі прийоми */
  }
  return {};
}

function saveMeals(meals: MealPick): void {
  try {
    localStorage.setItem(MEALS_KEY, JSON.stringify(meals));
  } catch {
    /* не запам'ятали — наступного разу знову всі */
  }
}

/**
 * Період обирається в межах завантаженого місяця: поза ним таблиця не знає
 * позначок, і перенесення повернуло б шаблон там, де наряд насправді стоїть.
 */
export function TransferDialog({
  people,
  dates: shown,
  min,
  max,
  onClose,
}: {
  people: Person[];
  /** Показані в таблиці дні — типовий період. */
  dates: IsoDate[];
  min: IsoDate;
  max: IsoDate;
  onClose: () => void;
}): ReactNode {
  const clamp = (d: IsoDate): IsoDate => (d < min ? min : d > max ? max : d);
  const [from, setFrom] = useState<IsoDate>(() => clamp(shown[0] ?? min));
  const [to, setTo] = useState<IsoDate>(() => clamp(shown[shown.length - 1] ?? max));
  // Закриті дні — ті самі, що в таблиці: перенесення їх не чіпає, поки замок
  // не знято (загальний чи замок самого дня).
  const isLocked = useLockCheck();
  const dates = useMemo(() => {
    const out: IsoDate[] = [];
    for (let d = from; d <= to; d = addDays(d, 1)) if (!isLocked(d)) out.push(d);
    return out;
  }, [from, to, isLocked]);

  const settings = useData(s => s.session!.org.settings);
  const index = useMarks(s => s.index);
  const apply = useMarks(s => s.apply);

  const kinds = useMemo(() => transferableKindsOf(settings), [settings]);
  const all = useMemo(() => [...kinds, AFTER_DUTY], [kinds]);
  const away = useMemo(() => new Set(awayKindsOf(settings.markTypes)), [settings]);
  const [pick, setPick] = useState(() => readPick(all));
  const [meals, setMeals] = useState<MealPick>(readMeals);
  /** Позначки, у яких розгорнуто прийоми їжі. */
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const mealsOf = useMemo(
    () => new Map(Object.entries(meals).map(([k, list]) => [k, new Set(list)] as const)),
    [meals],
  );

  // Добові рапорти — з таблиці «Звільнення о/с». Беремо й день перед першим
  // показаним: добове з останнього дня минулого місяця закінчується в цьому.
  const [dutyDays, setDutyDays] = useState<Array<{ personId: string; date: IsoDate }>>([]);
  const first = dates[0];
  const last = dates[dates.length - 1];
  useEffect(() => {
    if (!first || !last) return;
    let alive = true;
    api
      .get<{ reports: Array<{ personId: string; date: IsoDate; kind: string }> }>(
        `/api/leave-reports/range?from=${addDays(first, -1)}&to=${last}`,
      )
      .then(res => alive && setDutyDays(res.reports.filter(r => r.kind === 'duty')))
      .catch(e => toast(errorText(e), 'err'));
    return () => {
      alive = false;
    };
  }, [first, last]);

  // Скільки клітинок змінить кожен пункт сам по собі — і всі обрані разом.
  const perKind = useMemo(
    () =>
      new Map([
        ...kinds.map(
          k => [k, transferIntoMealsOps(people, dates, index, settings, new Set([k]), [], mealsOf).length] as const,
        ),
        [AFTER_DUTY, transferIntoMealsOps(people, dates, index, settings, new Set(), dutyDays).length] as const,
      ]),
    [kinds, people, dates, index, settings, dutyDays, mealsOf],
  );
  const ops = useMemo(
    () =>
      transferIntoMealsOps(
        people,
        dates,
        index,
        settings,
        pick,
        pick.has(AFTER_DUTY) ? dutyDays : [],
        mealsOf,
      ),
    [people, dates, index, settings, pick, dutyDays, mealsOf],
  );

  /** Увімкнути чи вимкнути прийом їжі для позначки. Усі ввімкнені — запис не потрібен. */
  const toggleMeal = (code: string, meal: number): void => {
    const all = settings.meals.map((_, i) => i);
    const current = new Set(meals[code] ?? all);
    if (current.has(meal)) current.delete(meal);
    else current.add(meal);
    const next = { ...meals };
    if (current.size === all.length) delete next[code];
    else next[code] = all.filter(i => current.has(i));
    setMeals(next);
    saveMeals(next);
  };

  const toggle = (code: string): void => {
    const next = new Set(pick);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setPick(next);
    savePick(next);
  };

  const run = (): void => {
    if (!ops.length) return;
    apply(ops);
    toast(`Перенесено: ${cellsText(ops.length)}. Скасувати — Ctrl+Z`, 'ok');
    onClose();
  };

  /**
   * Прийоми їжі позначки в тому порядку, як вона лягає. Наряд заступає ввечері
   * й іде через ніч — тож «Вечеря», а за нею «Сніданок» і «Обід» наступного
   * дня. Відсутність займає цілу добу — від сніданку. Позначка на кілька діб
   * (бр) повторює ті самі прийоми щодня, тож день біля них не пишемо.
   */
  const blockMeals = (t: MarkTypeDef): Array<{ meal: number; name: string; nextDay: boolean }> => {
    const count = settings.meals.length;
    const absence = away.has(t.code);
    const start = absence ? 0 : startMealOf(t, count);
    const spread = absence ? count : paintSpreadOf(t, count);
    const out: Array<{ meal: number; name: string; nextDay: boolean }> = [];
    for (let i = 0; i < spread && out.length < count; i++) {
      const meal = (start + i) % count;
      if (out.some(x => x.meal === meal)) continue;
      const nextDay = spread <= count && start + i >= count;
      out.push({ meal, name: settings.meals[meal]?.name ?? '', nextDay });
    }
    return out;
  };

  /**
   * Прийоми їжі одним рядком: «вечеря, сніданок і обід наст. дня». Обрано не
   * все — «лише вечеря»: так видно, що позначка переноситься не повністю.
   */
  const mealsText = (t: MarkTypeDef, chosen: Set<number>): string => {
    const block = blockMeals(t).filter(b => chosen.has(b.meal));
    if (!block.length) return 'жодного прийому їжі';
    const join = (names: string[]): string =>
      names.length > 1 ? `${names.slice(0, -1).join(', ')} і ${names[names.length - 1]}` : (names[0] ?? '');
    const lower = (b: { name: string }): string => b.name.toLowerCase();
    const today = block.filter(b => !b.nextDay).map(lower);
    const next = block.filter(b => b.nextDay).map(lower);
    const text = [join(today), next.length ? `${join(next)} наст. дня` : ''].filter(Boolean).join(', ');
    const many = !away.has(t.code) && paintSpreadOf(t, settings.meals.length) > settings.meals.length;
    const partial = block.length < blockMeals(t).length;
    return `${partial ? 'лише ' : ''}${text}${many ? ' щодня' : ''}`;
  };

  const toggleOpen = (code: string): void => {
    const next = new Set(open);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setOpen(next);
  };

  /** Літера позначки в її кольорі. */
  const letter = (t: MarkTypeDef): ReactNode => (
    <span className="transfer-letter" style={{ background: `var(--mk-${t.code}-bg)`, color: `var(--mk-${t.code}-fg)` }}>
      {t.letter}
    </span>
  );

  /**
   * Рядок позначки: що, скільки клітинок і які прийоми їжі. Прийоми — описом;
   * кнопки з'являються, лише коли їх розгорнули.
   */
  const row = (t: MarkTypeDef): ReactNode => {
    const n = perKind.get(t.code) ?? 0;
    const on = pick.has(t.code);
    const all = blockMeals(t);
    const chosen = new Set(meals[t.code] ?? settings.meals.map((_, i) => i));
    const partial = all.some(b => !chosen.has(b.meal));
    const expanded = open.has(t.code);
    return (
      <div key={t.code} className={`transfer-item${on ? '' : ' off'}`}>
        <label className="checkbox transfer-kind">
          <input type="checkbox" checked={on} onChange={() => toggle(t.code)} />
          {letter(t)}
          <span className="transfer-label">{t.label}</span>
          <span className="transfer-count">{cellsText(n)}</span>
        </label>
        {on ? (
          <button
            type="button"
            className={`transfer-meals-sum${partial ? ' partial' : ''}`}
            aria-expanded={expanded}
            onClick={() => toggleOpen(t.code)}
          >
            {mealsText(t, chosen)}
            <span className="chev" aria-hidden="true">
              {expanded ? '▴' : '▾'}
            </span>
          </button>
        ) : null}
        {on && expanded ? (
          <div className="transfer-meals" role="group" aria-label={`Прийоми їжі: ${t.label}`}>
            {all.map(({ meal, name, nextDay }) => {
              const label = `${name}${nextDay ? ' (наст. день)' : ''}`;
              return (
                <button
                  key={meal}
                  type="button"
                  className={`btn sm${chosen.has(meal) ? ' on' : ''}`}
                  aria-pressed={chosen.has(meal)}
                  title={
                    chosen.has(meal)
                      ? `${label}: ставити «${t.letter}» — натисніть, щоб не чіпати`
                      : `${label}: не чіпати — натисніть, щоб ставити «${t.letter}»`
                  }
                  onClick={() => toggleMeal(t.code, meal)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const types = kinds.map(code => settings.markTypes.find(t => t.code === code)).filter((t): t is MarkTypeDef => !!t);
  const counted = settings.markTypes.find(t => t.counted && !t.dutyOnly);
  const afterDuty = perKind.get(AFTER_DUTY) ?? 0;
  const busy = types.filter(t => (perKind.get(t.code) ?? 0) > 0);
  const idle = types.filter(t => !perKind.get(t.code));
  const duties = busy.filter(t => !away.has(t.code));
  const absences = busy.filter(t => away.has(t.code));

  return (
    <Modal
      title="Перенесення"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn primary" disabled={!ops.length} onClick={run}>
            {ops.length ? `Перенести · ${ops.length}` : 'Нічого переносити'}
          </button>
        </>
      }
    >
      <Field label="Період">
        <div className="period-row">
          <DateInput
            value={from}
            base={to}
            onChange={d => {
              if (!d) return;
              const next = clamp(d);
              setFrom(next);
              if (next > to) setTo(next);
            }}
          />
          <span className="period-dash">–</span>
          <DateInput
            value={to}
            base={from}
            onChange={d => {
              if (!d) return;
              const next = clamp(d);
              setTo(next);
              if (next < from) setFrom(next);
            }}
          />
        </div>
      </Field>
      {duties.length ? (
        <div className="transfer-group">
          <h3>Наряди</h3>
          {duties.map(row)}
        </div>
      ) : null}
      {absences.length ? (
        <div className="transfer-group">
          <h3>Статус о/с</h3>
          {absences.map(row)}
        </div>
      ) : null}
      {counted && afterDuty ? (
        <div className="transfer-group">
          <h3>Звільнення о/с</h3>
          <div className={`transfer-item${pick.has(AFTER_DUTY) ? '' : ' off'}`}>
            <label className="checkbox transfer-kind">
              <input type="checkbox" checked={pick.has(AFTER_DUTY)} onChange={() => toggle(AFTER_DUTY)} />
              {letter(counted)}
              <span
                className="transfer-label"
                title="Добове тягнеться до ранку: за сніданок наступного дня — компенсація"
              >
                Компенсація за ранок після добового
              </span>
              <span className="transfer-count">{cellsText(afterDuty)}</span>
            </label>
          </div>
        </div>
      ) : null}
      {idle.length ? (
        <p className="transfer-idle">
          <span className="muted">Без змін:</span>
          {idle.map(t => (
            <span key={t.code} title={t.label}>
              {letter(t)}
            </span>
          ))}
        </p>
      ) : null}
    </Modal>
  );
}
